import React, {
  MutableRefObject,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Divider, Tour, TourProps } from 'antd';
import { ArrowRightOutlined, CheckOutlined } from '@ant-design/icons';
import {
  selectCurrentUserId,
  selectSessionSelector,
} from '@mm/state/selectors/userSelector';
import { useSelector } from 'react-redux';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { selectActiveCompanyId } from '@mm/state/selectors/viewSelectors';
import tourImage from './xray-flow.png';
import styles from './XRayLeadsTour.module.css';
import classNames from 'classnames';
import { useResolutionInfo } from '@mm/hooks';
import { MOBILE_THRESHOLD } from '../../../../../constants/screenSize';
import moment from 'moment';

export type XRayLeadsTourRefs = {
  installationGuideRef: MutableRefObject<HTMLDivElement | null>;
  verifyInputRef: MutableRefObject<HTMLInputElement | null>;
  triggerEditorRef: MutableRefObject<HTMLButtonElement | null>;
};

export const TOURS_DATE_START_FROM = '2024-11-20';

type Props = { open?: boolean };

export const XRayLeadsTour = React.forwardRef<XRayLeadsTourRefs, Props>(
  (props, ref) => {
    const companyId = useSelector(selectActiveCompanyId);
    const currentUserId = useSelector(selectCurrentUserId);
    const session = useSelector(selectSessionSelector);
    const userAttributes = session?.userAttributes;
    const isNewUser = moment(session.createdAt) > moment(TOURS_DATE_START_FROM);

    const { screenWidth } = useResolutionInfo();
    const isMobile = screenWidth <= MOBILE_THRESHOLD;

    const installationGuideRef = useRef<HTMLDivElement | null>(null);
    const verifyInputRef = useRef<HTMLInputElement | null>(null);
    const triggerEditorRef = useRef<HTMLButtonElement | null>(null);

    const { updateUserAttributes } = useUsersCRUDActions();

    useImperativeHandle(ref, () => ({
      installationGuideRef,
      verifyInputRef,
      triggerEditorRef,
    }));

    const [open, setOpen] = useState<boolean>(false);

    const showTour =
      !userAttributes?.seenTour?.xray && props.open && !isMobile && isNewUser;

    const steps: TourProps['steps'] = [
      {
        title: (
          <div className={styles.stepTitle}>
            Follow the guide to install the X-Ray Lead Generator Pixel
          </div>
        ),
        description: (
          <div className={styles.stepDescription}>
            <img src={tourImage} style={{ width: 'fit-content' }} />
            Gather leads from your website traffic
            <Divider className={styles.divider} />
          </div>
        ),
        target: installationGuideRef.current,
        placement: 'bottomRight',
        nextButtonProps: {
          children: (
            <div>
              <ArrowRightOutlined /> Next
            </div>
          ),
          className: styles.nextButton,
        },
        prevButtonProps: {
          className: styles.prevButton,
        },
      },
      {
        title: (
          <div className={styles.stepTitle}>
            Check if the pixel is installed correctly
          </div>
        ),
        description: (
          <div className={styles.stepDescription}>
            Input your website URL to check if the script is working.
            <Divider className={styles.divider} />
          </div>
        ),
        target: verifyInputRef.current,
        placement: 'bottom',
        nextButtonProps: {
          children: (
            <div>
              <ArrowRightOutlined /> Next
            </div>
          ),
          className: styles.nextButton,
        },
        prevButtonProps: {
          className: styles.prevButton,
        },
      },
      {
        title: (
          <div className={styles.stepTitle}>
            Set up pages and conditions for your pixel to capture leads
          </div>
        ),
        description: (
          <div className={styles.stepDescription}>
            Capture leads from all of your website traffic or use filters to
            only capture on specific pages or after certain actions
            <Divider className={styles.divider} />
          </div>
        ),
        placement: 'right',
        nextButtonProps: {
          children: (
            <div>
              <CheckOutlined /> Done
            </div>
          ),
          className: classNames(styles.nextButton, styles.doneButton),
        },
        prevButtonProps: {
          className: styles.prevButton,
        },
        target: triggerEditorRef.current,
      },
    ];

    useEffect(() => {
      if (showTour) {
        setOpen(true);
        updateUserAttributes(companyId, currentUserId, {
          seenTour: { ...userAttributes.seenTour, xray: true },
        });
      }
    }, []);

    if (!open) {
      return <></>;
    }

    return (
      <Tour
        rootClassName={styles.tour}
        open={open}
        onClose={() => setOpen(false)}
        indicatorsRender={(current, total) => (
          <span className={styles.indicator}>
            {current + 1} of {total}
          </span>
        )}
        steps={steps}
        mask
      />
    );
  },
);
